'use client';

import { useEffect, useMemo, useRef } from 'react';

/**
 * This code is copied and modified from the following source:
 * Author: WorkOS https://workos.com/
 * Source: https://github.com/radix-ui/primitives/blob/main/packages/react/use-callback-ref/src/useCallbackRef.tsx
 * License: MIT https://github.com/radix-ui/primitives/blob/main/LICENSE
 */

/**
 * Hook that converts a callback function to a stable reference that doesn't change between renders.
 * Prevents unnecessary re-renders when passed as props and avoids re-executing effects when used
 * as dependencies. The returned function always calls the latest version of the callback.
 *
 * @template T - The type signature of the callback function.
 * @param {T | undefined} callback - The callback function to create a stable reference for.
 * @returns {T} A stable reference to the callback that always calls the latest version.
 *
 * @example
 * const handleChange = useCallbackRef(props.onChange);
 */
export function useCallbackRef<T extends (...args: Array<any>) => any>(callback: T | undefined): T {
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  });

  // https://github.com/facebook/react/issues/19240
  return useMemo(() => ((...args) => callbackRef.current?.(...args)) as T, []);
}
